// @ts-nocheck
/**
 * mediaRenderers.ts — Template V2 M3 Media / Visual Component Renderers
 *
 * Exports typed ComponentRenderer implementations for:
 *   IMAGE_RENDERER  — 'image'
 *   RATING_RENDERER — 'rating'
 *   COLOR_RENDERER  — 'color'
 *
 * Image renderer delegates AssetRef resolution to AssetResolver so that asset
 * policy (inline size caps, allowlist) is applied consistently (DEC-010).
 *
 * No React/RN imports; output is a plain serializable RenderDescriptor.
 */

import type { AssetRef } from '../../types/canonical';
import type { ValueSource } from '../../types/binding';
import { resolveAsset } from '../assets/AssetResolver';
import type { AssetResolutionContext } from '../assets/types';
import { applyExpressionFallback, buildDescriptor, resolveValue } from './rendererUtils';
import { componentInteractionProps } from './interactionProps';
import { resolveRatingGlyphId } from './ratingGlyphs';
import type { CompiledPropsByType, ComponentRenderer } from './types';
import { extractTypedProps } from './types';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Discriminates AssetRef from ValueSource<string> by kind. */
function isAssetRef(source: unknown): source is AssetRef {
  if (source == null || typeof source !== 'object') return false;
  const kind = (source as { kind?: string }).kind;
  return kind === 'inline' || kind === 'manifest' || kind === 'relativePath';
}

/** Default conservative asset context (DEC-010). */
const DEFAULT_ASSET_CONTEXT: AssetResolutionContext = {
  remoteUrlAllowlist: [],
  allowInlineAssets: true,
  allowRelativePaths: true,
};

/**
 * Validates a CSS-style color string: hex (#RGB, #RRGGBB, #RGBA, #RRGGBBAA)
 * or functional rgba(r,g,b,a). Returns true for valid strings.
 */
function isValidColorString(value: string): boolean {
  if (/^#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)) return true;
  if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(value)) return true;
  return false;
}

// ── Image ─────────────────────────────────────────────────────────────────────

export const IMAGE_RENDERER: ComponentRenderer<CompiledPropsByType['image']> = {
  supportedType: 'image',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('image', context.node.props);
    const assetCtx: AssetResolutionContext = context.assetContext ?? DEFAULT_ASSET_CONTEXT;

    let uri: string | null = null;
    let assetStatus: string = 'none';

    const source = p.image.source;
    if (source == null) {
      context.issues.push({
        severity: 'warning',
        code: 'schema.missing_required_field',
        message: `Image source is missing for node "${context.node.id}"; using fallback policy.`,
        path: context.node.id,
      });
    } else if (isAssetRef(source)) {
      const result = resolveAsset(source, assetCtx);
      assetStatus = result.status;
      if (result.status === 'resolved') {
        uri = result.data;
      } else {
        // Blocked / missing / unsupported — emit diagnostic; uri stays null
        context.issues.push({
          severity: 'warning',
          code: 'schema.missing_required_field',
          message: `Image asset could not be resolved for node "${context.node.id}": ${result.status}.`,
          path: context.node.id,
        });
        // Propagate resolver's own diagnostics
        for (const issue of result.issues) {
          context.issues.push(issue);
        }
      }
    } else {
      // ValueSource<string>
      uri = resolveValue(source, context) ?? null;
      assetStatus = uri !== null ? 'resolved' : 'none';
      if (uri === null && source.kind === 'binding') {
        context.issues.push({
          severity: 'warning',
          code: 'semantic.invalid_data_ref',
          message:
            'Image binding could not resolve a URI for node "' + context.node.id + '"; using fallback policy.',
          path: context.node.id,
        });
      }
    }

    const hrefRaw = p.image?.href ? resolveValue(p.image.href, context) : null;
    const href =
      typeof hrefRaw === 'string' && hrefRaw.trim().length > 0 ? hrefRaw.trim() : null;

    return buildDescriptor(context.node, visible, enabled, {
      uri,
      assetStatus,
      href,
      fit: p.image.fit ?? 'contain',
      editable: p.image.editable ?? false,
      fallback: p.image.fallback ?? 'placeholder',
      animate: context.bindingContext?.item?.animateImages === true,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── SVG (layout decorative) ───────────────────────────────────────────────────

function resolveSvgPaintColor(
  colorSource: CompiledPropsByType['svg']['svg']['fill'] | CompiledPropsByType['svg']['svg']['stroke'],
  fieldLabel: 'fill' | 'stroke',
  context: Parameters<ComponentRenderer<CompiledPropsByType['svg']>['render']>[0],
): string | null {
  if (colorSource == null) {
    return null;
  }
  const raw = resolveValue(colorSource, context);
  if (raw === undefined || raw === null) {
    return null;
  }
  if (typeof raw === 'string' && isValidColorString(raw)) {
    return raw;
  }
  context.issues.push({
    severity: 'warning',
    code: 'schema.invalid_field_type',
    message:
      `Svg ${fieldLabel} "${String(raw)}" is not a valid CSS color for node "${context.node.id}"; ` +
      'leaving authored SVG paint unchanged for this field.',
    path: context.node.id,
  });
  return null;
}

function resolveSvgStrokeWidthPx(
  strokeWidthSource: CompiledPropsByType['svg']['svg']['strokeWidth'],
  context: Parameters<ComponentRenderer<CompiledPropsByType['svg']>['render']>[0],
): number | null {
  if (strokeWidthSource == null) {
    return null;
  }
  const raw = resolveValue(strokeWidthSource, context);
  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
    return raw;
  }
  if (raw !== undefined && raw !== null) {
    context.issues.push({
      severity: 'warning',
      code: 'schema.invalid_field_type',
      message:
        `Svg strokeWidth "${String(raw)}" is not a non-negative number for node "${context.node.id}"; ` +
        'leaving authored stroke-width unchanged.',
      path: context.node.id,
    });
  }
  return null;
}

export const SVG_RENDERER: ComponentRenderer<CompiledPropsByType['svg']> = {
  supportedType: 'svg',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('svg', context.node.props);
    const assetCtx: AssetResolutionContext = context.assetContext ?? DEFAULT_ASSET_CONTEXT;

    let svgContent: string | null = null;
    let svgUri: string | null = null;
    let assetStatus: string = 'none';

    const result = resolveAsset(p.svg.source, assetCtx);
    assetStatus = result.status;
    if (result.status === 'resolved' && result.data != null) {
      if (p.svg.source.kind === 'relativePath') {
        svgUri = result.data;
      } else {
        svgContent = result.data;
      }
    } else if (result.status !== 'resolved') {
      context.issues.push({
        severity: 'warning',
        code: 'schema.missing_required_field',
        message: `SVG asset could not be resolved for node "${context.node.id}": ${result.status}.`,
        path: context.node.id,
      });
      for (const issue of result.issues) {
        context.issues.push(issue);
      }
    }

    const fillValue = resolveSvgPaintColor(p.svg.fill, 'fill', context);
    const strokeValue = resolveSvgPaintColor(p.svg.stroke, 'stroke', context);
    const strokeWidthPx = resolveSvgStrokeWidthPx(p.svg.strokeWidth, context);

    return buildDescriptor(context.node, visible, enabled, {
      svgContent,
      svgUri,
      assetStatus,
      fit: p.svg.fit ?? 'contain',
      pointerEvents: p.svg.pointerEvents ?? 'none',
      fillValue,
      strokeValue,
      strokeWidthPx,
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Rating ────────────────────────────────────────────────────────────────────

const RATING_DEFAULT_MAX = 5;

/**
 * Resolve rating max stars from ValueSource or legacy bare number.
 * Returns a positive integer (floored); falls back to 5 when missing/invalid.
 */
export function resolveRatingMaxStars(
  raw: ValueSource<number> | number | undefined,
  resolve: (source: ValueSource<number>) => unknown,
): number {
  let value: unknown = raw;
  if (raw != null && typeof raw === 'object' && 'kind' in raw) {
    value = resolve(raw);
  }
  const numeric =
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim() !== ''
        ? Number(value)
        : null;
  if (numeric == null || !Number.isFinite(numeric) || numeric < 1) {
    return RATING_DEFAULT_MAX;
  }
  return Math.max(1, Math.floor(numeric));
}

export const RATING_RENDERER: ComponentRenderer<CompiledPropsByType['rating']> = {
  supportedType: 'rating',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('rating', context.node.props);
    const raw = resolveValue(p.rating.source, context) as unknown;
    const maxRating = resolveRatingMaxStars(
      p.rating.maxRating as ValueSource<number> | number | undefined,
      (source) => resolveValue(source, context),
    );
    const step = p.rating.step ?? 1;
    const glyph = resolveRatingGlyphId(p.rating.glyph);

    let value: number | null = null;
    const numericRaw = typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : null;
    if (numericRaw !== null && Number.isFinite(numericRaw)) {
      if (numericRaw < 0 || numericRaw > maxRating) {
        const clamped = Math.max(0, Math.min(maxRating, numericRaw));
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message:
            `Rating value ${numericRaw} out of range [0, ${maxRating}] for node "${context.node.id}"; ` +
            `clamped to ${clamped}.`,
          path: context.node.id,
        });
        value = clamped;
      } else {
        value = numericRaw;
      }
    }

    return buildDescriptor(context.node, visible, enabled, {
      value,
      maxRating,
      step,
      editable: p.rating.editable ?? false,
      glyph,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};

// ── Color ─────────────────────────────────────────────────────────────────────
// Canvas adapter paints the layout box with the resolved color (no hex label).
// See docs/V2-Authoring-Guide.md → "Color components".

export const COLOR_RENDERER: ComponentRenderer<CompiledPropsByType['color']> = {
  supportedType: 'color',
  render(context) {
    const issuesBefore = context.issues.length;
    const { visible, enabled } = applyExpressionFallback(context);
    const p = extractTypedProps('color', context.node.props);
    const raw = resolveValue(p.color.source, context);

    let value: string | null = null;
    if (raw !== undefined) {
      if (isValidColorString(raw)) {
        value = raw;
      } else {
        context.issues.push({
          severity: 'warning',
          code: 'schema.invalid_field_type',
          message:
            `Color value "${raw}" is not a valid CSS color for node "${context.node.id}"; ` +
            'falling back to null.',
          path: context.node.id,
        });
      }
    }

    return buildDescriptor(context.node, visible, enabled, {
      value,
      format: p.color.format ?? 'hex',
      editable: p.color.editable ?? false,
      ...componentInteractionProps(context.node),
    }, { diagnostics: context.issues.slice(issuesBefore), renderContext: context });
  },
};
